import { useState, useEffect } from "react";
import { Search20Regular } from "@fluentui/react-icons";
import { useNavigate, useParams } from "react-router";
import { useRootStore } from "store";
import { useInboxReplyContext } from "app/store/InboxReplyContext";
import UnsavedChangesModal from "components/shared/unsavedChangesModal/UnsavedChangesModal";
import { useDebounce } from "hooks/use-debounce";
import { InboxType } from "models/inbox/inbox";
import { useRequiredCustomerId } from "hooks/use-customer-id";
import "./SearchTab.scss";

export default function SearchTab() {
  const navigate = useNavigate();
  const customerId = useRequiredCustomerId();
  const { inboxType } = useParams<{ inboxType: InboxType }>();

  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [pendingSearchTerm, setPendingSearchTerm] = useState("");

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const { isReplying, isDirty, setIsReplying, clearDirty } = useInboxReplyContext();
  const shouldShowModal = isReplying && isDirty;

  const {
    fetchMyThreads,
    fetchAllThreads,
    setSentMessagesSearchTerm,
    sentMessagesSearchTerm
  } = useRootStore((state) => state.inbox);

  const handleModalConfirm = () => {
    setIsReplying(false);
    clearDirty();
    performSearch(pendingSearchTerm);
    setShowModal(false);
    setPendingSearchTerm("");
  };

  const handleModalCancel = () => {
    setSearchTerm("");
    setShowModal(false);
    setPendingSearchTerm("");
  };

  const performSearch = (term: string) => {
    fetchMyThreads(customerId, 0, term);
    fetchAllThreads(customerId, 0, term);
    setSentMessagesSearchTerm(term);

    // Navigate to list view to show search results
    const targetInboxType = inboxType || "my";
    navigate(`/customers/${customerId}/inbox/${targetInboxType}`);
  };

  const handleExpand = () => {
    setIsExpanded(true);
  };

  const handleCollapse = () => {
    setIsExpanded(false);

    // If there's a search term, clear it and reset search
    if (searchTerm) {
      setSearchTerm("");
      setSentMessagesSearchTerm("");
      fetchMyThreads(customerId, 0, "");
      fetchAllThreads(customerId, 0, "");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  // Debounced search effect
  useEffect(() => {
    // don't send requests if search term is unchanged
    if (sentMessagesSearchTerm === debouncedSearchTerm) {
      return;
    }

    // Only search when we have a customer ID
    if (customerId) {
      if (shouldShowModal) {
        setPendingSearchTerm(debouncedSearchTerm);
        setShowModal(true);
      } else {
        performSearch(debouncedSearchTerm);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, customerId]);

  return (
    <div
      className={`search-tab-container ${isExpanded ? "expanded" : "collapsed"}`}
      onClick={!isExpanded ? handleExpand : undefined}
      role={!isExpanded ? "button" : undefined}
      aria-label={!isExpanded ? "Expand search" : undefined}
      tabIndex={!isExpanded ? 0 : undefined}
      onKeyDown={!isExpanded ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleExpand();
        }
      } : undefined}
    >
      <div
        className="search-tab-button"
        aria-label="Search"
        aria-expanded={isExpanded}
      >
        <div className="search-tab-content">
          <div
            className="search-icon-wrapper"
            onClick={isExpanded ? handleCollapse : undefined}
            role={isExpanded ? "button" : undefined}
            aria-label={isExpanded ? "Collapse search" : undefined}
            tabIndex={isExpanded ? 0 : -1}
            onKeyDown={isExpanded ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleCollapse();
              }
            } : undefined}
          >
            <Search20Regular />
          </div>
          <input
            type="text"
            className="search-input"
            placeholder="Sök"
            aria-label="Search input"
            value={searchTerm}
            onChange={handleSearchChange}
            autoFocus={isExpanded}
            disabled={!isExpanded}
            tabIndex={isExpanded ? 0 : -1}
          />
        </div>
      </div>
      
      {showModal && (
        <UnsavedChangesModal
          text="Du har ändringar som ej sparats i ditt svar. Är du säker på att du vill söka och lämna utan att spara?"
          onConfirm={handleModalConfirm}
          onCancel={handleModalCancel}
          onDismiss={handleModalCancel}
        />
      )}
    </div>
  );
}
